import {
  Avatar,
  Badge,
  Body1,
  Caption1,
  Card,
  CardHeader,
  mergeClasses,
  Text,
} from '@fluentui/react-components';
import type { AvatarProps } from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';

import type {
  TranscriptDocument,
  TranscriptTurn,
} from '../../utils/parseTranscript.js';

import { useStyles } from './TranscriptViewer.style.js';

export interface TranscriptViewerProps {
  transcript: TranscriptDocument;
  ariaLabel?: string;
  isScrollable?: boolean;
  /**
   * When true, the viewer stretches to fill its parent's height and the
   * timeline scrolls internally, instead of being capped to a fixed preview
   * height. Use inside a full-height container (e.g. a tab body).
   */
  fillHeight?: boolean;
  /** Optional heading rendered inline with the message count. */
  heading?: ReactNode;
  /** Optional action rendered right-aligned on the heading/summary row. */
  headerAction?: ReactNode;
  /** Optional hover-revealed action rendered inside each turn. */
  renderTurnAction?: (turn: TranscriptTurn, index: number) => ReactNode;
  /** Optional content rendered directly below each turn (e.g. anchored forms). */
  renderTurnAttachment?: (turn: TranscriptTurn, index: number) => ReactNode;
}

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const TIME_SEGMENT_LENGTH = 2;
const AVATAR_SIZE = 32;
const SPEAKER_COLORS = [
  'dark-red',
  'royal-blue',
  'dark-green',
  'purple',
  'pumpkin',
  'teal',
  'magenta',
  'steel',
  'cranberry',
  'forest',
  'grape',
  'brass',
] as const satisfies readonly NonNullable<AvatarProps['color']>[];

type SpeakerColor = (typeof SPEAKER_COLORS)[number];

function formatElapsedTime(seconds: number): string {
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(
    totalSeconds / (SECONDS_PER_MINUTE * MINUTES_PER_HOUR),
  );
  const minutes = Math.floor(
    (totalSeconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR,
  );
  const remainingSeconds = totalSeconds % SECONDS_PER_MINUTE;
  const segments = [
    minutes.toString().padStart(TIME_SEGMENT_LENGTH, '0'),
    remainingSeconds.toString().padStart(TIME_SEGMENT_LENGTH, '0'),
  ];

  if (hours > 0) {
    segments.unshift(hours.toString().padStart(TIME_SEGMENT_LENGTH, '0'));
  }

  return segments.join(':');
}

function getTurnPosition(turn: TranscriptTurn, index: number): string {
  if (turn.timestamp !== null) {
    return new Date(turn.timestamp).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  return turn.startSeconds === null
    ? `Turn ${index + 1}`
    : formatElapsedTime(turn.startSeconds);
}

function getEventLabel(turn: TranscriptTurn): string | null {
  if (turn.eventType === 'chat') {
    return 'Chat';
  }

  return turn.eventType === 'userQuery' ? 'Agent request' : null;
}

function getSpeakerColors(turns: TranscriptTurn[]): Map<string, SpeakerColor> {
  const colors = new Map<string, SpeakerColor>();

  for (const turn of turns) {
    if (!colors.has(turn.speaker)) {
      const colorIndex = colors.size % SPEAKER_COLORS.length;
      colors.set(turn.speaker, SPEAKER_COLORS[colorIndex]!);
    }
  }

  return colors;
}

export function TranscriptViewer({
  transcript,
  ariaLabel = 'Meeting transcript',
  isScrollable = false,
  fillHeight = false,
  heading,
  headerAction,
  renderTurnAction,
  renderTurnAttachment,
}: TranscriptViewerProps) {
  const styles = useStyles();
  const speakerColors = useMemo(
    () => getSpeakerColors(transcript.turns),
    [transcript.turns],
  );

  return (
    <Card
      className={mergeClasses(
        styles.root,
        fillHeight ? styles.rootFill : undefined,
      )}
      appearance={fillHeight ? 'subtle' : undefined}
      role="region"
      aria-label={ariaLabel}
    >
      <div className={styles.summaryRow}>
        {heading !== undefined && (
          <Text className={styles.headingText}>{heading}</Text>
        )}
        <Caption1 className={styles.summary}>
          {transcript.turns.length}{' '}
          {transcript.turns.length === 1 ? 'message' : 'messages'}
        </Caption1>
        {headerAction !== undefined && (
          <div className={styles.headerAction}>{headerAction}</div>
        )}
      </div>

      {transcript.turns.length === 0 ? (
        <Text className={styles.empty}>This transcript has no messages.</Text>
      ) : (
        <Card
          className={mergeClasses(
            styles.timeline,
            isScrollable && !fillHeight ? styles.scrollableTimeline : undefined,
            fillHeight ? styles.fillTimeline : undefined,
          )}
          appearance={fillHeight ? 'subtle' : undefined}
          role="list"
        >
          {transcript.turns.map((turn, index) => (
            <Fragment key={turn.id}>
              <Card
                className={mergeClasses(
                  styles.turn,
                  turn.eventType === 'chat' ? styles.chatTurn : undefined,
                  turn.eventType === 'userQuery'
                    ? styles.userQueryTurn
                    : undefined,
                )}
                role="listitem"
                appearance="subtle"
              >
                <Caption1 className={styles.position}>
                  {getTurnPosition(turn, index)}
                </Caption1>
                <CardHeader
                  className={styles.message}
                  image={
                    <Avatar
                      name={turn.speaker}
                      size={AVATAR_SIZE}
                      color={speakerColors.get(turn.speaker)}
                      data-speaker-color={speakerColors.get(turn.speaker)}
                    />
                  }
                  header={{
                    className: styles.heading,
                    children: (
                      <>
                        <Text className={styles.speaker}>{turn.speaker}</Text>
                        {getEventLabel(turn) !== null && (
                          <Badge
                            appearance={
                              turn.eventType === 'userQuery' ? 'filled' : 'tint'
                            }
                            color={
                              turn.eventType === 'userQuery'
                                ? 'brand'
                                : 'informative'
                            }
                            size="small"
                          >
                            {getEventLabel(turn)}
                          </Badge>
                        )}
                      </>
                    ),
                  }}
                  description={
                    <Body1 className={styles.text}>{turn.text}</Body1>
                  }
                />
                {renderTurnAction !== undefined && (
                  <div
                    className={styles.actionSlot}
                    data-expected-action-add=""
                  >
                    {renderTurnAction(turn, index)}
                  </div>
                )}
              </Card>
              {renderTurnAttachment !== undefined && (
                <div className={styles.attachmentSlot}>
                  {renderTurnAttachment(turn, index)}
                </div>
              )}
            </Fragment>
          ))}
        </Card>
      )}
    </Card>
  );
}
